/**
 * Fait : la LISTE des messages d'un fil — chaque message avec son bouton
 *   (Modifier sur le sien, Régénérer sur une réponse), l'édition en place,
 *   et la génération EN COURS qui grandit bloc par bloc avec son bouton
 *   Annuler. L'ordre vient des rangs, l'affichage ne réordonne rien.
 *
 * Ne fait pas : elle n'appelle aucune API et ne décide d'aucun droit — le
 *   parent (`PageFil`) reçoit les gestes et agit.
 */

import { Champ } from '../gabarits/Champ.tsx';
import type { MessageVu } from './api-fil.ts';

/** Une génération en cours d'affichage — le texte grandit bloc par bloc. */
export interface GenerationVisible {
  readonly generationId: string;

  /** La branche qui émet : l'écran n'affiche que celle qu'il montre. */
  readonly brancheId: string;

  readonly texte: string;
}

/** L'édition en place d'un message — le sien. */
export interface EditionEnCours {
  readonly id: string;
  readonly texte: string;
  readonly version: number;
}

export function MessagesDuFil({
  messages,
  enCours,
  enEdition,
  surEdition,
  surEnregistrement,
  surRegeneration,
  surAnnulation,
}: {
  readonly messages: readonly MessageVu[];
  readonly enCours: GenerationVisible | null;
  readonly enEdition: EditionEnCours | null;
  surEdition: (edition: EditionEnCours | null) => void;
  surEnregistrement: () => void;
  surRegeneration: (messageDOrigineId: string) => void;
  surAnnulation: () => void;
}) {
  return (
    <ol data-liste="messages" aria-live="off">
      {messages.map((message) => (
        <li key={message.id} data-origine={message.origine} className="station-du-fil">
          {/*
           * Le NUMÉRO est le repère de position dans le fil. Il est masqué aux
           * lecteurs d'écran : la liste est déjà ordonnée, et l'annoncer une
           * seconde fois ne ferait que doubler chaque message.
           */}
          {/*
           * `rang + 1` : le rang est compté à partir de zéro côté données, et
           * un humain compte à partir de un. Le premier échange s'affichait
           * « 0 ».
           */}
          <span className="numero-du-fil" aria-hidden="true">
            {message.rang + 1}
          </span>
          <p className="qui-parle">
            {message.origine === 'membre' ? 'La question' : 'La réponse de l’atelier'}
          </p>
          {enEdition?.id === message.id ? (
            <span data-role="edition">
              <Champ
                libelle="Votre message"
                value={enEdition.texte}
                onChange={(evenement) => {
                  surEdition({ ...enEdition, texte: evenement.target.value });
                }}
              />
              <button type="button" onClick={surEnregistrement}>
                Enregistrer
              </button>
            </span>
          ) : (
            <>
              <p data-role="texte" className="dit">
                {message.texte}
              </p>
              {/*
               * LES ACTIONS ONT LEUR PROPRE LIGNE, sous le message et séparées
               * par un filet. Elles étaient posées à la suite du texte, sur la
               * même ligne que lui : c'est ce qui faisait le plus « HTML brut »
               * dans le retour de Charles du 2026-08-21.
               */}
              <div className="actions-du-message">
                {message.origine === 'membre' && (
                  <button
                    type="button"
                    data-role="modifier"
                    onClick={() => {
                      surEdition({
                        id: message.id,
                        texte: message.texte,
                        version: message.version,
                      });
                    }}
                  >
                    Modifier
                  </button>
                )}
                {message.origine === 'assistant' && (
                  <button
                    type="button"
                    data-role="regenerer"
                    onClick={() => {
                      const precedent = messages.find(
                        (candidat) => candidat.rang === message.rang - 1,
                      );
                      if (precedent !== undefined) surRegeneration(precedent.id);
                    }}
                  >
                    Régénérer
                  </button>
                )}
              </div>
            </>
          )}
        </li>
      ))}

      {enCours !== null && (
        <li
          data-origine="assistant"
          data-role="en-cours"
          aria-live="polite"
          className="station-du-fil"
        >
          <span className="numero-du-fil" aria-hidden="true">
            …
          </span>
          <p className="qui-parle">L’atelier écrit</p>
          <p data-role="texte" className="dit">
            {enCours.texte}
          </p>
          <div className="actions-du-message">
            <button type="button" data-role="annuler" onClick={surAnnulation}>
              Annuler
            </button>
          </div>
        </li>
      )}
    </ol>
  );
}
